import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("pa_token");
    if (!token) {
      setUser(null);
      setMembership(null);
      setLoading(false);
      return null;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setMembership(data.membership);
      if (!data.user) localStorage.removeItem("pa_token");
      return data;
    } catch (e) {
      setUser(null);
      setMembership(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const applyAuth = async (data) => {
    localStorage.setItem("pa_token", data.token);
    setUser(data.user);
    setMembership(null);
    await refresh();
    return data.user;
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    return await applyAuth(data);
  };

  const register = async (email, password, extra = {}) => {
    const { data } = await api.post("/auth/register", { email, password, ...extra });
    const authedUser = await applyAuth(data);
    return {
      user: authedUser,
      trialActivated: Boolean(data.trialActivated),
      trialEndsAt: data.trialEndsAt || "",
    };
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      // Best effort: the server-side logout is advisory; the local session is cleared below.
      console.warn("Server logout call failed:", error?.message || error);
    }
    localStorage.removeItem("pa_token");
    setUser(null);
    setMembership(null);
  };

  return (
    <AuthContext.Provider value={{ user, membership, loading, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
